import React, { useState, useEffect } from "react";
import { Shield, Check, Clock, AlertTriangle, XCircle, Search, RefreshCw } from "lucide-react";

export interface ScanStage {
  id: number;
  label: string;
  description: string;
  minProgress: number;
}

export const SCAN_STAGES: ScanStage[] = [
  {
    id: 1,
    label: "Validating domain",
    description: "Input normalization, canonical TLD syntax, and registrable domain mapping",
    minProgress: 14,
  },
  {
    id: 2,
    label: "Identifying company profile",
    description: "Resolving organization identity, sector classification, and threat profile",
    minProgress: 28,
  },
  {
    id: 3,
    label: "Checking domain and subdomains",
    description: "Mapping exposure surface and expanding search query criteria",
    minProgress: 42,
  },
  {
    id: 4,
    label: "Searching threat intelligence sources",
    description: "Querying dark web forums, ransomware victim logs, and breach archives",
    minProgress: 60,
  },
  {
    id: 5,
    label: "Correlating matches and threat indicators",
    description: "Analyzing direct records, forum mentions, credentials, and threat actors",
    minProgress: 75,
  },
  {
    id: 6,
    label: "Evaluating severity and sector context",
    description: "Calculating severity distribution, risk score, and sector benchmarks",
    minProgress: 88,
  },
  {
    id: 7,
    label: "Final verification and preparing results",
    description: "Deduplicating findings, formatting evidence snippets, and rendering report",
    minProgress: 98,
  },
];

interface ScanningRadarPanelProps {
  domain: string;
  currentStageIndex: number; // 0 to 6
  isFinalizing: boolean; // Stage 7 reached
  onCancel?: () => void;
  onRetry?: () => void;
  hasError?: boolean;
  errorMessage?: string;
}

export const ScanningRadarPanel: React.FC<ScanningRadarPanelProps> = ({
  domain,
  currentStageIndex,
  isFinalizing,
  onCancel,
  onRetry,
  hasError,
  errorMessage,
}) => {
  const [elapsedSec, setElapsedSec] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Elapsed time counter
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSec((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 5-4-3-2-1 Final verification countdown
  useEffect(() => {
    if (isFinalizing && countdown === null) {
      setCountdown(5);
    }
  }, [isFinalizing, countdown]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const cdTimer = setTimeout(() => {
        setCountdown((c) => (c !== null && c > 0 ? c - 1 : 0));
      }, 1000);
      return () => clearTimeout(cdTimer);
    }
  }, [countdown]);

  const activeStage = SCAN_STAGES[Math.min(currentStageIndex, SCAN_STAGES.length - 1)];
  const progressPercent = activeStage ? activeStage.minProgress : 15;

  const formatTime = (totalSec: number) => {
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Estimated remaining time based on stage
  const estimatedRemainingSec = Math.max(0, 10 - elapsedSec);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: "linear-gradient(180deg, #0e121a 0%, #090c10 100%)",
        border: "1px solid #1e293b",
        borderRadius: "16px",
        padding: "36px 28px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: "760px",
        margin: "24px auto",
        width: "100%",
        boxShadow: "0 20px 45px -10px rgba(0, 0, 0, 0.6), 0 0 30px rgba(16, 185, 129, 0.05)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes radarSweep {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes radarPulse {
          0% { transform: scale(0.9); opacity: 0.8; }
          50% { transform: scale(1.08); opacity: 0.3; }
          100% { transform: scale(0.9); opacity: 0.8; }
        }
        @keyframes subtleGlow {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.3)); }
          50% { filter: drop-shadow(0 0 16px rgba(16, 185, 129, 0.6)); }
        }
        .radar-sweep-beam {
          animation: radarSweep 3.2s linear infinite;
          transform-origin: 90px 90px;
        }
        .radar-pulse-ring {
          animation: radarPulse 2.8s ease-in-out infinite;
          transform-origin: 90px 90px;
        }
        @media (prefers-reduced-motion: reduce) {
          .radar-sweep-beam, .radar-pulse-ring {
            animation: none !important;
          }
        }
      `}</style>

      {/* Background Decorative Grid */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          pointerEvents: "none",
          opacity: 0.6,
        }}
      />

      {/* Target Domain Badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 14px",
          borderRadius: "20px",
          background: "rgba(16, 185, 129, 0.1)",
          border: "1px solid rgba(16, 185, 129, 0.3)",
          color: "#10b981",
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "0.5px",
          marginBottom: "24px",
          zIndex: 1,
        }}
      >
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#10b981",
            boxShadow: "0 0 8px #10b981",
          }}
        />
        <span>SCANNING DOMAIN: <strong style={{ color: "#f8fafc" }}>{domain}</strong></span>
      </div>

      {/* Animated Radar Sweep & Shield */}
      <div
        style={{
          position: "relative",
          width: "180px",
          height: "180px",
          marginBottom: "28px",
          zIndex: 1,
        }}
      >
        <svg width="180" height="180" viewBox="0 0 180 180">
          <defs>
            <radialGradient id="radarSweepGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
              <stop offset="70%" stopColor="#14b8a6" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#334155" stopOpacity="0.2" />
            </linearGradient>
          </defs>

          {/* Outer ring */}
          <circle cx="90" cy="90" r="84" fill="none" stroke="url(#ringGradient)" strokeWidth="1.5" strokeDasharray="4 4" />
          {/* Middle pulsing ring */}
          <circle cx="90" cy="90" r="62" fill="none" stroke="#10b981" strokeWidth="1" opacity="0.3" className="radar-pulse-ring" />
          {/* Inner ring */}
          <circle cx="90" cy="90" r="40" fill="none" stroke="#10b981" strokeWidth="1" opacity="0.4" />
          {/* Crosshairs */}
          <line x1="90" y1="6" x2="90" y2="174" stroke="#1e293b" strokeWidth="1" strokeDasharray="2 3" />
          <line x1="6" y1="90" x2="174" y2="90" stroke="#1e293b" strokeWidth="1" strokeDasharray="2 3" />

          {/* Rotating Sweep Beam */}
          <g className="radar-sweep-beam">
            <path
              d="M 90 90 L 90 6 A 84 84 0 0 1 174 90 Z"
              fill="url(#radarSweepGradient)"
              opacity="0.8"
            />
            <line x1="90" y1="90" x2="174" y2="90" stroke="#34d399" strokeWidth="2" strokeLinecap="round" />
          </g>
        </svg>

        {/* Center Shield Icon */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
          }}
        >
          <Shield size={24} color="#0b0f17" strokeWidth={2.5} />
        </div>
      </div>

      {/* Final Countdown Banner (When Stage 7 active) */}
      {isFinalizing && countdown !== null && (
        <div
          style={{
            marginBottom: "20px",
            padding: "8px 20px",
            borderRadius: "30px",
            background: "rgba(16, 185, 129, 0.15)",
            border: "1px solid rgba(16, 185, 129, 0.4)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            zIndex: 1,
          }}
        >
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#34d399" }}>
            Final verification:
          </span>
          <span
            style={{
              fontSize: "20px",
              fontWeight: 800,
              fontFamily: "monospace",
              color: "#10b981",
              minWidth: "24px",
              textAlign: "center",
            }}
          >
            {countdown > 0 ? countdown : "Ready"}
          </span>
          <span style={{ fontSize: "11px", color: "#94a3b8" }}>
            {countdown > 0 ? "Assembling findings..." : "Displaying threat report"}
          </span>
        </div>
      )}

      {/* Current Stage Label & Description */}
      <div style={{ textAlign: "center", marginBottom: "20px", zIndex: 1, maxWidth: "560px" }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#f8fafc",
            margin: "0 0 6px 0",
            letterSpacing: "-0.3px",
          }}
        >
          {activeStage.label}
        </h2>
        <p
          style={{
            fontSize: "13px",
            color: "#94a3b8",
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {activeStage.description}
        </p>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          width: "100%",
          maxWidth: "500px",
          height: "6px",
          background: "#1e293b",
          borderRadius: "3px",
          overflow: "hidden",
          marginBottom: "16px",
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: `${progressPercent}%`,
            height: "100%",
            background: "linear-gradient(90deg, #10b981 0%, #14b8a6 100%)",
            borderRadius: "3px",
            transition: "width 0.4s ease-out",
            boxShadow: "0 0 10px rgba(16, 185, 129, 0.5)",
          }}
        />
      </div>

      {/* Elapsed Time & Indicators */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "500px",
          fontSize: "12px",
          color: "#64748b",
          marginBottom: "28px",
          zIndex: 1,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Clock size={13} color="#10b981" />
          Elapsed: <strong style={{ color: "#cbd5e1" }}>{formatTime(elapsedSec)}</strong>
        </span>
        <span>
          Stage {activeStage.id} of {SCAN_STAGES.length}
        </span>
        <span>
          {isFinalizing ? "Finalizing" : `Est. remaining: ~${estimatedRemainingSec}s`}
        </span>
      </div>

      {/* Stages Timeline Checklist */}
      <div
        style={{
          width: "100%",
          maxWidth: "540px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          zIndex: 1,
        }}
      >
        {SCAN_STAGES.map((stg, idx) => {
          const isDone = idx < currentStageIndex;
          const isActive = idx === currentStageIndex;
          return (
            <div
              key={stg.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px 12px",
                borderRadius: "8px",
                background: isActive
                  ? "rgba(16, 185, 129, 0.08)"
                  : isDone
                  ? "rgba(30, 41, 59, 0.3)"
                  : "transparent",
                border: isActive
                  ? "1px solid rgba(16, 185, 129, 0.25)"
                  : "1px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              {/* Status Circle */}
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isDone
                    ? "#10b981"
                    : isActive
                    ? "transparent"
                    : "#1e293b",
                  border: isActive ? "2px solid #10b981" : "none",
                  flexShrink: 0,
                }}
              >
                {isDone ? (
                  <Check size={12} color="#0f172a" strokeWidth={3} />
                ) : isActive ? (
                  <span
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#10b981",
                      boxShadow: "0 0 6px #10b981",
                    }}
                  />
                ) : (
                  <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                    {stg.id}
                  </span>
                )}
              </div>

              {/* Stage Title */}
              <span
                style={{
                  fontSize: "12.5px",
                  fontWeight: isActive ? 600 : 400,
                  color: isDone ? "#94a3b8" : isActive ? "#f8fafc" : "#475569",
                  flex: 1,
                }}
              >
                {stg.label}
              </span>

              {/* Tag indicator */}
              {isActive && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#10b981",
                    background: "rgba(16, 185, 129, 0.15)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                  }}
                >
                  ACTIVE
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Long search notice (>12 seconds) */}
      {elapsedSec >= 12 && !hasError && (
        <div
          style={{
            marginTop: "20px",
            padding: "10px 16px",
            borderRadius: "8px",
            background: "rgba(234, 179, 8, 0.08)",
            border: "1px solid rgba(234, 179, 8, 0.2)",
            color: "#facc15",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            maxWidth: "540px",
            zIndex: 1,
          }}
        >
          <AlertTriangle size={15} color="#facc15" />
          <span>
            Deep darknet search in progress. Correlating across massive leak archives and forum posts.
          </span>
        </div>
      )}

      {/* Error state if any */}
      {hasError && (
        <div
          style={{
            marginTop: "20px",
            padding: "12px 18px",
            borderRadius: "8px",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            color: "#f87171",
            fontSize: "13px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxWidth: "540px",
            zIndex: 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600 }}>
            <XCircle size={16} color="#ef4444" />
            <span>Search encountered a delay or issue</span>
          </div>
          <span style={{ fontSize: "12px", color: "#fca5a5" }}>
            {errorMessage || "Unable to complete search request. You may retry or cancel."}
          </span>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                alignSelf: "flex-start",
                marginTop: "4px",
                background: "#ef4444",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <RefreshCw size={12} /> Retry Search
            </button>
          )}
        </div>
      )}

      {/* Safe Cancel Button */}
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          style={{
            marginTop: "24px",
            background: "transparent",
            color: "#64748b",
            border: "1px solid #334155",
            borderRadius: "6px",
            padding: "6px 16px",
            fontSize: "12px",
            cursor: "pointer",
            transition: "all 0.15s ease",
            zIndex: 1,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#f87171";
            e.currentTarget.style.borderColor = "#ef4444";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#64748b";
            e.currentTarget.style.borderColor = "#334155";
          }}
        >
          Cancel Scan
        </button>
      )}
    </div>
  );
};
